import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ButtonStyled } from "@nike/nike-design-system-components";
import SpurTypeCell from "./SpurTypeCell";
import PgStatusChip from "./PgStatusChip";
import ShipToCell from "./ShipToCell";
import {
  PgFilterDropdown,
  PgFilterTextField,
  PgFilterActions,
} from "./PgFilterField";
import { pgDefaultColDef, pgGridOptions, pgRowCheckboxColDef } from "./pgGridConfig";
import {
  DEFAULT_PAGE_SIZE,
  PgTablePagination,
  slicePage,
} from "./pgPagination";
import { PG_FILTER_ALL, isFilterAll } from "./pgFilterConstants";
import {
  SPUR_DISPLAY_STATUS,
  SPUR_STATUS_FILTER_OPTIONS,
} from "../../constants/spurDisplayStatus";
import {
  pgGridRowClickToggleProps,
  useLimitedGridSelection,
} from "./pgGridSelection";
import { usePgGridColumnFit } from "./pgGridResize";

function buildSpurDropdownOptions(rows, receivingZone) {
  const pool = isFilterAll(receivingZone)
    ? rows
    : rows.filter((r) => r.receivingZone === receivingZone);
  const seen = new Set();
  const spurs = [];
  for (const row of pool) {
    const spur = row?.spur;
    if (!spur || seen.has(spur)) {
      continue;
    }
    seen.add(spur);
    spurs.push({ label: spur, value: spur });
  }
  spurs.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  return [
    {
      label: isFilterAll(receivingZone)
        ? "All Spurs"
        : `All ${receivingZone} Spurs`,
      value: PG_FILTER_ALL,
    },
    ...spurs,
  ];
}

const ZONE_OPTIONS = [
  { label: "All Zones", value: PG_FILTER_ALL },
  { label: "Receiving 1", value: "Receiving 1" },
  { label: "Receiving 2", value: "Receiving 2" },
];

function spurHasAssignedPalletGroups(data) {
  return (data?.palletGroupCount ?? 0) > 0;
}

function PalletGroupCell({ value, data, onMultiplePgs }) {
  if (!value || value === "—") {
    return <span className="pg-cell-dim">—</span>;
  }
  if (spurHasAssignedPalletGroups(data)) {
    return (
      <button
        type="button"
        className="pg-link-cell"
        onClick={(e) => {
          e.stopPropagation();
          onMultiplePgs(data);
        }}
      >
        {value}
      </button>
    );
  }
  return <span className="pg-cell-strong">{value}</span>;
}

function SpurSummarySection({
  rowData,
  spurCatalogRows = [],
  totalCount = 0,
  onFetchSpur,
  isFilterLoading = false,
  onSelectionChanged,
  onUnassign,
  onReassign,
  onComplete,
  spurSelectedLabel,
  spurActionDisabled,
  onMultiplePgs,
  maxSelection,
  selectionResetKey = 0,
}) {
  const [receivingZone, setReceivingZone] = useState(PG_FILTER_ALL);
  const [spurId, setSpurId] = useState(PG_FILTER_ALL);
  const [inboundId, setInboundId] = useState("");
  const [spurStatus, setSpurStatus] = useState(PG_FILTER_ALL);
  const [appliedFilters, setAppliedFilters] = useState({
    receivingZone: "",
    spurId: "",
    inboundId: "",
    spurStatus: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const gridApiRef = useRef(null);

  useEffect(() => {
    if (selectionResetKey > 0) {
      gridApiRef.current?.deselectAll();
    }
  }, [selectionResetKey]);

  const usesServerPagination = Boolean(onFetchSpur);

  const spurOptions = useMemo(() => {
    const catalogSource =
      spurCatalogRows.length > 0
        ? spurCatalogRows
        : usesServerPagination
          ? []
          : rowData;
    const source = catalogSource.length > 0 ? catalogSource : rowData;
    return buildSpurDropdownOptions(source, receivingZone);
  }, [spurCatalogRows, rowData, receivingZone, usesServerPagination]);

  const filteredRows = useMemo(() => {
    if (usesServerPagination) {
      return rowData;
    }
    return rowData.filter((row) => {
      if (
        appliedFilters.receivingZone &&
        row.receivingZone !== appliedFilters.receivingZone
      ) {
        return false;
      }
      if (appliedFilters.spurId && row.spur !== appliedFilters.spurId) {
        return false;
      }
      if (
        appliedFilters.inboundId &&
        row.inboundId.toLowerCase() !== appliedFilters.inboundId.toLowerCase()
      ) {
        return false;
      }
      if (appliedFilters.spurStatus) {
        if (
          appliedFilters.spurStatus === SPUR_DISPLAY_STATUS.READY_TO_COMPLETE
        ) {
          if (!row.readyToComplete) return false;
        } else if (appliedFilters.spurStatus === SPUR_DISPLAY_STATUS.ASSIGNED) {
          if (
            row.spurStatus !== SPUR_DISPLAY_STATUS.ASSIGNED ||
            row.readyToComplete
          ) {
            return false;
          }
        } else if (row.spurStatus !== appliedFilters.spurStatus) {
          return false;
        }
      }
      return true;
    });
  }, [rowData, appliedFilters, usesServerPagination]);

  const { pageRows, totalPages, page: safePage, totalItems } = useMemo(() => {
    if (usesServerPagination) {
      const totalPagesForServer = Math.max(1, Math.ceil(totalCount / pageSize));
      const safePageForServer = Math.min(Math.max(1, page), totalPagesForServer);
      return {
        pageRows: filteredRows,
        totalPages: totalPagesForServer,
        page: safePageForServer,
        totalItems: totalCount,
      };
    }
    return slicePage(filteredRows, page, pageSize);
  }, [filteredRows, page, pageSize, totalCount, usesServerPagination]);

  useEffect(() => {
    setPage(safePage);
  }, [safePage]);

  const columnDefs = useMemo(
    () => [
      { ...pgRowCheckboxColDef },
      {
        headerName: "Spur",
        field: "spur",
        minWidth: 108,
        flex: 1.05,
        cellClass: "pg-cell-strong",
      },
      {
        headerName: "Spur Type",
        field: "spurType",
        minWidth: 56,
        flex: 0.35,
        cellRenderer: (p) => <SpurTypeCell value={p.value} />,
      },
      {
        headerName: "Spur Status",
        field: "spurStatus",
        minWidth: 108,
        flex: 0.75,
        cellRenderer: (p) => (
          <PgStatusChip
            status={p.data.spurStatus}
            readyToComplete={p.data.readyToComplete}
          />
        ),
      },
      {
        headerName: "Pallet Group",
        field: "palletGroup",
        minWidth: 100,
        flex: 1,
        cellRenderer: (p) => (
          <PalletGroupCell
            value={p.value}
            data={p.data}
            onMultiplePgs={onMultiplePgs}
          />
        ),
      },
      {
        headerName: "Inbound ID",
        field: "inboundId",
        minWidth: 88,
        flex: 0.65,
      },
      {
        headerName: "Total Cartons",
        field: "totalCartons",
        minWidth: 80,
        flex: 0.55,
      },
      { headerName: "Diverted", field: "diverted", minWidth: 64, flex: 0.45 },
      {
        headerName: "Palletised",
        field: "palletised",
        minWidth: 72,
        flex: 0.48,
      },
      {
        headerName: "Ship to",
        field: "shipTo",
        minWidth: 96,
        flex: 0.9,
        cellRenderer: (p) => <ShipToCell data={p.data} />,
        cellClass: "pg-cell-ship-to",
        headerClass: "pg-grid-header-cell pg-grid-header-cell--ship-to",
      },
    ],
    [onMultiplePgs],
  );

  const onGridSelectionChanged = useLimitedGridSelection(
    onSelectionChanged,
    maxSelection,
    (row) => row.id,
  );

  const gridColumnFitProps = usePgGridColumnFit(pageRows);

  const buildFilterPayload = useCallback(
    () => ({
      receivingZone: isFilterAll(receivingZone) ? "" : receivingZone,
      spur: isFilterAll(spurId) ? "" : spurId,
      inboundId: inboundId.trim(),
      spurStatus: isFilterAll(spurStatus) ? "" : spurStatus,
    }),
    [receivingZone, spurId, inboundId, spurStatus],
  );

  const applyFilters = async () => {
    const nextFilters = {
      receivingZone: isFilterAll(receivingZone) ? "" : receivingZone,
      spurId: isFilterAll(spurId) ? "" : spurId,
      inboundId: inboundId.trim(),
      spurStatus: isFilterAll(spurStatus) ? "" : spurStatus,
    };
    setAppliedFilters(nextFilters);
    setPage(1);
    if (onFetchSpur) {
      await onFetchSpur({
        filters: buildFilterPayload(),
        page: 1,
        pageSize,
      });
    }
  };

  const resetFilters = async () => {
    setReceivingZone(PG_FILTER_ALL);
    setSpurId(PG_FILTER_ALL);
    setInboundId("");
    setSpurStatus(PG_FILTER_ALL);
    setAppliedFilters({
      receivingZone: "",
      spurId: "",
      inboundId: "",
      spurStatus: "",
    });
    setPage(1);
    if (onFetchSpur) {
      await onFetchSpur({
        filters: {
          receivingZone: "",
          spur: "",
          inboundId: "",
          spurStatus: "",
        },
        page: 1,
        pageSize,
      });
    }
  };

  const handlePageChange = async (nextPage) => {
    setPage(nextPage);
    if (onFetchSpur) {
      await onFetchSpur({
        filters: buildFilterPayload(),
        page: nextPage,
        pageSize,
      });
    }
  };

  const handlePageSizeChange = async (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(1);
    if (onFetchSpur) {
      await onFetchSpur({
        filters: buildFilterPayload(),
        page: 1,
        pageSize: nextPageSize,
      });
    }
  };

  return (
    <>
      <h2 className="pg-panel-title">Spur Summary</h2>
      <div className="pg-filters">
        <div className="pg-filter-grid">
          <PgFilterDropdown
            label="Receiving Zone"
            options={ZONE_OPTIONS}
            value={receivingZone}
            onChange={(e) => {
              const zone = e.target.value;
              setReceivingZone(zone);
              if (!isFilterAll(zone) && !isFilterAll(spurId)) {
                const row = rowData.find((r) => r.spur === spurId);
                if (row && row.receivingZone !== zone) {
                  setSpurId(PG_FILTER_ALL);
                }
              }
            }}
            ariaLive="polite"
          />
          <PgFilterDropdown
            label="Spur ID"
            options={spurOptions}
            value={spurId}
            onChange={(e) => setSpurId(e.target.value)}
            ariaLive="polite"
          />
          <PgFilterTextField
            label="Inbound ID"
            placeholder="Exact Inbound ID — e.g. IBD0012845"
            value={inboundId}
            onChange={(e) => setInboundId(e.target.value)}
            ariaLive="polite"
          />
          <PgFilterDropdown
            label="Spur Status"
            options={SPUR_STATUS_FILTER_OPTIONS}
            value={spurStatus}
            onChange={(e) => setSpurStatus(e.target.value)}
            ariaLive="polite"
          />
          <PgFilterActions>
            <ButtonStyled
              appearance="primary"
              type="button"
              className="pg-btn-apply"
              onClick={applyFilters}
              disabled={isFilterLoading}
            >
              {isFilterLoading ? "Applying…" : "Apply"}
            </ButtonStyled>
            <ButtonStyled
              appearance="primary"
              type="button"
              className="pg-btn-reset"
              onClick={resetFilters}
              disabled={isFilterLoading}
            >
              Reset
            </ButtonStyled>
          </PgFilterActions>
        </div>
      </div>
      <div className="pg-table-wrap">
        <div className="pg-grid-wrap ag-theme-alpine ag-theme-pg">
          <AgGridReact
            rowData={pageRows}
            columnDefs={columnDefs}
            defaultColDef={pgDefaultColDef}
            {...pgGridOptions}
            {...gridColumnFitProps}
            rowSelection="multiple"
            {...pgGridRowClickToggleProps}
            onSelectionChanged={onGridSelectionChanged}
            onGridReady={(params) => {
              gridApiRef.current = params.api;
            }}
            getRowId={(p) => p.data.id}
            rowClassRules={{
              "pg-row-pending": (p) =>
                p.data.spurStatus === SPUR_DISPLAY_STATUS.ASSIGNED ||
                p.data.spurStatus === SPUR_DISPLAY_STATUS.SUSPENDED,
              "pg-row-normal": (p) =>
                p.data.spurStatus !== SPUR_DISPLAY_STATUS.ASSIGNED &&
                p.data.spurStatus !== SPUR_DISPLAY_STATUS.SUSPENDED,
              "pg-row-selected": (p) => p.node.isSelected(),
            }}
            overlayNoRowsTemplate='<span class="pg-grid-empty">No spurs match filters</span>'
          />
        </div>
      </div>
      <footer className="pg-panel-footer">
        <span className="pg-selection-hint">{spurSelectedLabel}</span>
        <PgTablePagination
          page={safePage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
        />
      </footer>
      <div className="pg-action-bar">
        <ButtonStyled
          appearance="primary"
          type="button"
          className="pg-btn-reset"
          disabled={spurActionDisabled.unassign}
          onClick={onUnassign}
        >
          Unassign
        </ButtonStyled>
        <ButtonStyled
          appearance="primary"
          type="button"
          className="pg-btn-reset"
          disabled={spurActionDisabled.reassign}
          onClick={onReassign}
        >
          Reassign
        </ButtonStyled>
        <ButtonStyled
          appearance="primary"
          type="button"
          className="pg-btn-apply"
          disabled={spurActionDisabled.complete}
          onClick={onComplete}
        >
          Complete
        </ButtonStyled>
      </div>
    </>
  );
}

export default SpurSummarySection;
